import React, { useLayoutEffect, useRef } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AssistantChatMessage } from '@hooks/useAssistantChat';
import RingilSpinner from '@components/ringil3/RingilSpinner/RingilSpinner';
import AssistantMessageBubble from './AssistantMessageBubble';
import { assistantMessageDate, isDifferentDay } from './assistantDate';

interface ComponentProps {
  messages: AssistantChatMessage[];
  hasMoreHistory: boolean;
  loadingHistory: boolean;
  userName: string;
  userInitials: string;
  onLoadOlder: () => void;
  onRetry: (text: string) => void;
  /** Sends a clarification choice as the next question. */
  onPickSuggestion: (text: string) => void;
}

/** Distance from the top at which scrolling up pulls the next (older) page. */
const LOAD_OLDER_THRESHOLD_PX = 48;
/** Within this of the bottom, the list counts as "following" the conversation and keeps up with it. */
const STICK_TO_BOTTOM_PX = 80;

const AssistantMessageList: React.FC<ComponentProps> = ({ messages, hasMoreHistory, loadingHistory, userName, userInitials, onLoadOlder, onRetry, onPickSuggestion }) => {
  const { tr } = useTranslationLgs();
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Scroll height captured just before an older page is requested, so the viewport can be restored. */
  const restoreFromHeight = useRef<number | null>(null);
  /** Whether the reader is at the bottom — an answer streaming in only scrolls the list if they are. */
  const followingRef = useRef(true);
  const messageCount = useRef(0);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    if (restoreFromHeight.current !== null) {
      // Older messages were prepended: keep the reader where they were instead of jumping.
      container.scrollTop += container.scrollHeight - restoreFromHeight.current;
      restoreFromHeight.current = null;
      return;
    }
    // A new turn (the user just asked) always brings the list down; text streaming into an answer only
    // does while the reader is following — scrolled up to re-read something, they stay put.
    const newTurn = messages.length !== messageCount.current;
    messageCount.current = messages.length;
    if (newTurn || followingRef.current) container.scrollTop = container.scrollHeight;
  }, [messages]);

  const onScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    followingRef.current = container.scrollHeight - container.scrollTop - container.clientHeight < STICK_TO_BOTTOM_PX;
    if (loadingHistory || !hasMoreHistory) return;
    if (container.scrollTop > LOAD_OLDER_THRESHOLD_PX) return;
    restoreFromHeight.current = container.scrollHeight;
    onLoadOlder();
  };

  return (
    <div ref={scrollRef} onScroll={onScroll} className="flex flex-1 flex-col gap-6 overflow-y-auto px-6 pb-6">
      {loadingHistory && (
        <div className="flex justify-center py-2">
          <RingilSpinner isSpinning={true} />
        </div>
      )}
      {!loadingHistory && hasMoreHistory && (
        <div className="py-1 text-center text-xs text-content-secondary">{tr('Assistant.scrollForOlder', 'Scroll up to load older messages')}</div>
      )}

      {messages.map((message, index) => {
        const previous = index > 0 ? messages[index - 1] : null;
        const showDaySeparator = previous ? isDifferentDay(previous.createdAt, message.createdAt) : false;
        const next = messages[index + 1];

        return (
          <React.Fragment key={message.id}>
            {showDaySeparator && <div className="pt-2 text-center text-xs tracking-[0.24px] text-content-primary">{assistantMessageDate(message.createdAt)}</div>}
            <AssistantMessageBubble
              message={message}
              userName={userName}
              userInitials={userInitials}
              onRetry={onRetry}
              onPickSuggestion={onPickSuggestion}
              // Only the answer the conversation ends on can still be followed up by picking a choice.
              suggestionsActive={!next}
              nextQuestion={next?.role === 'USER' ? next.content : undefined}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default AssistantMessageList;
